/**
 * Files Cache Module - Orchestrator for file caching
 * 
 * Phase 4: File Management Migration
 * Extends and enhances file caching logic from main.js
 * 
 * Refactored into modules:
 * - local-storage-manager.js: localStorage operations
 * - files-loader.js: file and summaries loading
 * - filter-metadata-generator.js: filter metadata generation
 */

import { LocalStorageManager } from './local-storage-manager.js';
import { FilesLoader } from './files-loader.js';
import { FilterMetadataGenerator } from './filter-metadata-generator.js';
import { lastModified, unzip } from '../local-storage.js';
import { fileLoader } from '../../files/file-loader.js';
import { fileProcessor } from '../../files/file-processor.js';
import { 
    isIdbSupported, 
    isIdbEnabled, 
    openDatabase, 
    closeDatabase, 
    getMeta as idbGetMeta, 
    setMeta as idbSetMeta, 
    clearFiles as idbClearFiles, 
    bulkUpsertFiles as idbBulkUpsert, 
    getAllFiles as idbGetAll, 
    getSummariesMeta as idbGetSummariesMeta, 
    setSummariesMeta as idbSetSummariesMeta, 
    bulkUpsertSummaries as idbBulkUpsertSummaries, 
    getSummariesByKeys as idbGetSummariesByKeys, 
    clearSummaries as idbClearSummaries, 
    getSummariesCount as idbGetSummariesCount 
} from '../idb-files.js';

/**
 * Files Cache Manager Class - Orchestrates file caching operations
 */
class FilesCacheManager {
    constructor() {
        this.localStorageManager = new LocalStorageManager();
        this.filterMetadataGenerator = new FilterMetadataGenerator();
        this.filesLoader = new FilesLoader(this.localStorageManager, this.filterMetadataGenerator);
        
        this.currentVersion = '4.0.0';
        
        // IDB module interface
        this.idbModule = {
            isIdbSupported,
            isIdbEnabled,
            openDatabase,
            closeDatabase,
            idbGetMeta,
            idbSetMeta,
            idbClearFiles,
            idbBulkUpsert,
            idbGetAll,
            idbGetSummariesMeta,
            idbSetSummariesMeta,
            idbBulkUpsertSummaries,
            idbGetSummariesByKeys,
            idbClearSummaries,
            idbGetSummariesCount
        };
    }
    
    /**
     * Get cached files date from localStorage
     * @returns {number|null} - Timestamp or null if invalid
     */
    getCachedDate() {
        return this.localStorageManager.getCachedDate();
    }
    
    /**
     * Set cached files date in localStorage
     * @param {number} timestamp - Timestamp to cache
     */
    setCachedDate(timestamp) {
        this.localStorageManager.setCachedDate(timestamp);
    }
    
    /**
     * Get cached files list from localStorage
     * @returns {Array|null} - Files array or null if invalid
     */
    getCachedFiles() {
        return this.localStorageManager.getCachedFiles(unzip);
    }
    
    /**
     * Set cached files list in localStorage
     * @param {string} compressedData - Compressed files data
     * @returns {boolean} - Success status
     */
    setCachedFiles(compressedData) {
        return this.localStorageManager.setCachedFiles(compressedData);
    }
    
    /**
     * Get cache metadata
     * @returns {Object|null} - Metadata object or null
     */
    getCacheMetadata() {
        return this.localStorageManager.getCacheMetadata();
    }
    
    /**
     * Set cache metadata
     * @param {Object} metadata - Metadata to store
     */
    setCacheMetadata(metadata) {
        this.localStorageManager.setCacheMetadata(metadata);
    }
    
    /**
     * Check if cache is valid based on server timestamp
     * @param {string} url - Files API URL
     * @returns {Promise<boolean>} - Cache validity status
     */
    async isCacheValid(url = '/api/files/') {
        return this.filesLoader.isCacheValid(url, lastModified, this.idbModule);
    }
    
    /**
     * Load files with intelligent caching
     * @param {Object} options - Loading options
     * @returns {Promise<Object>} - Result with files and metadata
     */
    async loadFiles(options = {}) {
        return this.filesLoader.loadFiles(options, lastModified, this.idbModule, fileLoader, fileProcessor);
    }

    /**
     * Check if summaries cache is valid (HEAD-only)
     * @param {string} url - Summaries API URL
     * @returns {Promise<boolean>} - Cache validity status
     */
    async isSummariesValid(url = '/api/files/summaries') {
        return this.filesLoader.isSummariesValid(url, lastModified, this.idbModule);
    }

    /**
     * Load summaries dataset with caching by Last-Modified, store meta in IDB.
     * Returns { summaries, source, timestamp, metaOnly? }
     * @param {Object} options - Loading options
     * @returns {Promise<Object>} - Result with summaries and metadata
     */
    async loadSummaries(options = {}) {
        return this.filesLoader.loadSummaries(options, lastModified, this.idbModule, fileLoader);
    }

    /**
     * Get summaries by keys, persisting new ones to IDB if provided in summariesSource.
     * @param {Array} keys - Keys to fetch
     * @param {Object} summariesSource - Source object with summaries
     * @returns {Promise<Array>} - Summaries array
     */
    async getSummariesByKeys(keys = [], summariesSource = null) {
        return this.filesLoader.getSummariesByKeys(keys, summariesSource, this.idbModule);
    }
    
    /**
     * Invalidate entire cache
     */
    invalidateCache() {
        this.localStorageManager.invalidateCache();
    }
    
    /**
     * Clear all localStorage (nuclear option)
     */
    clearAllStorage() {
        this.localStorageManager.clearAllStorage();
    }
    
    /**
     * Get cache statistics
     * @returns {Object} - Statistics object
     */
    getStats() {
        return this.localStorageManager.getStats();
    }
    
    /**
     * Get approximate cache size in bytes
     * @returns {number} - Cache size
     */
    getCacheSize() {
        return this.localStorageManager.getCacheSize();
    }
    
    /**
     * Reset statistics
     */
    resetStats() {
        this.localStorageManager.resetStats();
    }
}

// Create singleton instance
const filesCacheManager = new FilesCacheManager();

// Export for use in other modules
export { filesCacheManager };

// Global export for backward compatibility
window.filesCacheManager = filesCacheManager;

